/* Coffee Bar Setup Tool, a full-screen drawer over the current page. Shell: snippets/bar-tool.liquid (rendered by the
   header on every page); data: <tool page url>?view=bar-data (templates/page.bar-data.liquid → snippets/bar-data.liquid),
   fetched on first open or preload and kept in memory; CSS: assets/bar-tool.css, injected on first open or preload.
   Two parts:
   - createModel(D, query): the bar as data (state, picks, cur, plan, saving, checkout and share URLs). No DOM;
     exported for node unit checks.
   - the drawer: one delegated click listener opens it from any plain click on a link to the tool page (entry links keep
     their real href); products on the page's [data-bar-product] cards are merged in before locking. Renders the top bar
     (title, setting / volume / power / water controls, close), [data-bar-app] (questions → bar → plan), the phone bar,
     the option sheet, the email sheet and the chat sheet; hands the chat to window.openChatWith (assets/footer.js:
     Tidio in staff hours, the night chat outside them) with a mailto fallback only when that is missing; dataLayer
     events. History: open pushes the page URL + bar params, changes replace it, Back closes;
     any page loaded with bar=1 (and the tool page itself) opens the drawer on load.
   r4: clicked tiles / tool links get a loading bar (.bt-ld.is-loading, min 350 ms) until the next screen renders; the
     top bar's groups keep fixed widths (measured once: --bt-volw / --bt-factw) with sliding pills; setting / volume /
     pick changes show a 300 ms skeleton (.is-skel) then fade in; the verdict line (rules, no network) tops the bar.
   r5: the verdict is a dark "Specialist check" card (volume verdict / station essential_note / labels.verdict_*);
     "Email me this plan" posts to Klaviyo (subscription + "Coffee Bar Plan Requested", same client calls as
     assets/collection-compare.js, key / list from bar-data "klaviyo"); the chat's first message is the question and
     the bar link only, the full bar goes to staff as context (openChatWith summary / url).
   r6: the verdict is a light, compact block ("Verdict:" + title, one reason line, a small clay action); an opening with
     a locked product (m / g) is "built around your machine": the locked piece shows first, then the matched stations
     one by one with the total counting up (first bar render of the opening only), the verdict reads
     labels.verdict_matched until the first change, the plan groups rows under labels.plan_yours / plan_matched and
     untouched matched tiles say labels.added_badge. Phone bar: a count / arrival / total row above the two buttons.
   r7: the drawer opens / closes with a slide only (stays opaque; only the contents fade); phones show the
     verdict_matched line (is-matched) instead of "Next we'd add" until the first change.
   r15: sheet tags are per product from the prices shown (labels.best / tag_value / tag_top / tag_popular).
   r16: one setup screen (U.need) before the bar whenever the drawer opens with a setting: the volume question as
     selectable tiles (a v in the link preselects one) + power / water; "Show my bar" sets the volume, then the bar
     renders as before. Skipped for links that carry bar state (bar=1 + v + m / c, or n=0), top-bar changes in the bar
     and history Back / Forward (except Back to the setup's own entry). History entries carry { bar: 1, d, need: { vol } }.
   r17: the setup screen asks only the volume (no "What do you already have?"); the bar opens with every station the
     data gives, and shoppers remove pieces with "I already have one" in each sheet.
   r18: a machine that doesn't suit the setting (setting defaults.fit: voltage list / max_groups; product vc / gc) gets
     the labels.verdict_misfit warning (after "no machine", before "missing essential") and hides the setting's machine
     note on its card; the setting's own machine picks never misfit.
   r20: verdict order no machine → misfit → small (one-tap "Switch to the {to}") → missing (a station marked "I already
     have one" is covered) → ready (the volume's title only for its default machine or the same group count, else
     labels.verdict_custom_title); water from tanks + a machine without a tank option: labels.tank_note on the card and as
     the ready reason; choices carry over setting / volume changes while still offered (labels.keep_note when one
     drops), a rule's change to another station (tamper ↔ grinder) toasts labels.swap_note with Undo; Freight row (the
     highest shipping tier fee) in every total; checkout adds to the cart (/cart/add.js) then /checkout?discount=; the
     tool page's Close goes back (or home).
   r21: the setup screen shows only when no volume is known; its volume tiles go straight to the bar (loading bar on
     the tile, then needGo: history push, so Back returns to the setup), no "Show my bar" button. A v in the link
     opens straight into the bar.
   r25: under the machine card two rows: "Fitted to your bar" (filled and "using my own" tiles, labels.row_fitted_*)
     and "What to add next" (open stations as compact "+" cards, labels.row_add_*, hidden when none). A station that
     moves up from the add row scrolls into view and flashes once.
   URL: bar=1, s setting, v volume, m locked machine, g locked espresso grinder, p power (110v / 240v), w water,
   c changed picks (<station>:<handle>, "-" = already have one), e entry (machine, grinder, collection, home, menu, header),
   n=0 the setup screen is done (every bar URL and Copy link carry it). */
(function () {
  'use strict';

  function fill(s, o) { return String(s == null ? '' : s).replace(/\{(\w+)\}/g, function (m, k) { return o && o[k] != null ? o[k] : m; }); }
  function nums(s) { var m = String(s == null ? '' : s).match(/\d+/g); return m ? m.map(Number) : []; }

  /* ================= model ================= */
  function createModel(D, query) {
    var P = D.products || {};
    var R = D.rule || {};
    var SETS = (D.settings || []).filter(function (s) { return s && s.key; });
    var SET = {}; SETS.forEach(function (s) { SET[s.key] = s; });
    var ST = (D.stations || []).filter(function (s) { return s && s.h; }).slice().sort(function (a, b) { return (Number(a.sort) || 0) - (Number(b.sort) || 0); });
    var STH = {}; ST.forEach(function (s) { STH[s.h] = s; });
    // The machine station is the one shown on top: the lowest sort (it is in no volume's filled / plus list)
    var MACH = ST.length ? ST[0].h : null;
    // The espresso grinder station: the one a picks rule links to, else a station's linked_to, else the next station
    var GR = null;
    ST.forEach(function (s) { var r = s.picks && s.picks.rule; if (!GR && r && r.linked_station && STH[r.linked_station]) GR = r.linked_station; });
    ST.forEach(function (s) { if (!GR && s.linked && STH[s.linked] && s.linked !== MACH) GR = s.linked; });
    if (!GR && ST[1]) GR = ST[1].h;

    var q = query || {};
    var S = {
      set: null, vol: null, power: null, water: null,
      chosen: {}, have: {}, added: {}, pin: {}, own: false,
      lockM: null, lockG: null, entry: q.e || '', viewing: {}
    };

    function ok(h) { return !!(h && P[h]); }
    function inStation(st, h) { var s = STH[st]; return !!(s && ok(h) && (s.products || []).indexOf(h) > -1); }
    function setting() { return SET[S.set] || null; }
    function volume() { var s = setting(); if (!s) return null; return (s.volumes || []).filter(function (v) { return v && v.key === S.vol; })[0] || null; }
    function lock(h) { return h === MACH ? S.lockM : h === GR ? S.lockG : null; }

    function applyDefaults() {
      var s = setting(), d = (s && s.defaults) || {};
      S.power = String(d.power || '').toLowerCase() || null;
      S.water = String(d.water || '').toLowerCase() || null;
    }

    // [good, best, step] for the current setting / volume / power; unavailable or unknown handles become null
    function picks(h) {
      var s = STH[h];
      if (!s || !s.picks || !S.set) return [null, null, null];
      var ps = s.picks[S.set] || {};
      var arr = (h === MACH && S.power === '110v' && ps['110v']) ? ps['110v'] : (ps[S.vol] || ps['*'] || []);
      arr = [0, 1, 2].map(function (i) { var x = arr[i]; return ok(x) && P[x].a !== false ? x : null; });
      var r = s.picks.rule;
      if (r && r.swap_best_to && ok(r.swap_best_to)) {
        var g = cur(r.linked_station || GR);
        if (!g || (r.when_grinder_not_in || []).indexOf(g) === -1) {
          var to = r.swap_best_to;
          arr = [arr[0] === to ? null : arr[0], to, arr[2] === to ? null : arr[2]];
        }
      }
      // Matched rule: the grinder on the bar picks Our pick; the data's Our pick moves to an empty Step up
      if (r && Array.isArray(r.match)) {
        var lk = r.linked_station || GR;
        var gm = lk && lk !== h ? cur(lk) : null;
        var hit = gm && r.match.filter(function (m) { return m && (m.grinders || []).indexOf(gm) > -1 && ok(m.best) && P[m.best].a !== false; })[0];
        if (hit) {
          var b = hit.best, step = arr[2] || (arr[1] !== b ? arr[1] : null);
          arr = [arr[0] === b ? null : arr[0], b, step === b ? null : step];
        }
      }
      return arr;
    }

    function machineEmpty() { return !!(S.lockG && !S.lockM && !S.chosen[MACH] && !S.pin[MACH]); }

    // The piece in a station now (null: left out)
    function cur(h) {
      if (S.have[h]) return null;
      if (h === MACH && (S.own || machineEmpty())) return null;
      var pk = picks(h), c = S.chosen[h];
      if (c && (pk.indexOf(c) > -1 || c === lock(h) || c === S.pin[h])) return c;
      if (S.pin[h] && ok(S.pin[h])) return S.pin[h];
      if (lock(h) && ok(lock(h))) return lock(h);
      return pk[1] || pk[0] || pk[2] || null;
    }

    // Station tiles under the machine: the volume's filled + plus, in sort order
    function shown() {
      var v = volume(); if (!v) return [];
      var f = v.filled || [], pl = v.plus || [];
      return ST.filter(function (s) { return s.h !== MACH && (f.indexOf(s.h) > -1 || pl.indexOf(s.h) > -1); }).map(function (s) { return s.h; });
    }
    function optional(h) { var v = volume(); return !!(v && (v.plus || []).indexOf(h) > -1 && (v.filled || []).indexOf(h) === -1); }
    function open(h) { return optional(h) && !S.added[h]; } // an empty "+" tile
    function inBar(h) {
      if (h === MACH) return !S.own && !machineEmpty() && !!cur(h);
      if (open(h)) return false;
      return !!cur(h);
    }
    function order() { return (MACH ? [MACH] : []).concat(shown()); }
    function items() { return order().filter(inBar).map(function (h) { return { h: h, id: cur(h) }; }); }
    function total(list) { return (list || items()).reduce(function (a, x) { return a + (Number(P[x.id].p) || 0); }, 0); }

    // Saving: floor(share × Σ headroom / step) × step, capped at min(rule cap, setting cap)
    function saving(list) {
      list = list || items();
      var sum = list.reduce(function (a, x) { return a + (Number(P[x.id].hr) || 0); }, 0);
      var step = Number(R.step) || 1;
      var sv = Math.floor((Number(R.share) || 0) * sum / step) * step;
      var caps = [R.cap, setting() && setting().cap].map(Number).filter(function (n) { return n > 0; });
      if (caps.length) sv = Math.min(sv, Math.min.apply(null, caps));
      return Math.max(0, sv);
    }
    // The code applies only from 100 and when the total (cents) reaches saving × min_order_multiple
    function code(list) {
      list = list || items();
      var sv = saving(list);
      if (sv < 100) return null;
      var c = (R.codes || {})[String(sv)];
      if (!c) return null;
      if (total(list) / 100 < sv * (Number(R.min_order_multiple) || 0)) return null;
      return { saving: sv, code: c };
    }

    // Freight (cents): the highest shipping tier fee in the bar (product "ship.r"); 0 = every piece ships free; null =
    // no piece has a known fee
    function freight(list) {
      list = list || items();
      var max = 0, known = 0;
      list.forEach(function (x) {
        var r = (P[x.id].ship || {}).r;
        if (r != null && r !== '' && !isNaN(Number(r))) { known++; max = Math.max(max, Number(r)); }
      });
      if (!list.length || (!max && known < list.length)) return null;
      return max;
    }

    // r20 checkout: the bar's variants go to /cart/add.js (quantity 1 each), then /checkout with the same code;
    // checkoutUrl() (the permalink, which replaces the cart) stays as the fallback
    function checkoutAdd() {
      var list = items(); if (!list.length) return null;
      var c = code(list), base = (D.root || '/').replace(/\/?$/, '/');
      return {
        items: list.map(function (x) { return { id: Number(P[x.id].v), quantity: 1 }; }),
        attributes: { _bar: S.set + '-' + S.vol },
        go: base + 'checkout' + (c ? '?discount=' + encodeURIComponent(c.code) : '')
      };
    }

    function checkoutUrl() {
      var list = items(); if (!list.length) return null;
      var c = code(list);
      var u = (D.root || '/').replace(/\/?$/, '/') + 'cart/' + list.map(function (x) { return P[x.id].v + ':1'; }).join(',') + '?';
      if (c) u += 'discount=' + encodeURIComponent(c.code) + '&';
      return u + 'attributes[_bar]=' + encodeURIComponent(S.set + '-' + S.vol);
    }

    // Share link: s, v, m, g, p, w and every changed station as c=<station>:<handle> ("-" = already have one)
    function shareQuery() {
      var o = [];
      if (S.set) o.push('s=' + encodeURIComponent(S.set));
      if (S.vol) o.push('v=' + encodeURIComponent(S.vol));
      if (S.lockM) o.push('m=' + encodeURIComponent(S.lockM));
      if (S.lockG) o.push('g=' + encodeURIComponent(S.lockG));
      var s = setting();
      if (s && s.askPower && S.power) o.push('p=' + encodeURIComponent(S.power));
      if (s && s.askWater && S.water) o.push('w=' + encodeURIComponent(S.water));
      var c = [];
      order().forEach(function (h) {
        if ((h === MACH && S.own) || S.have[h]) c.push(h + ':-');
        else if (S.chosen[h] && cur(h) === S.chosen[h] && S.chosen[h] !== lock(h)) c.push(h + ':' + S.chosen[h]);
        else if (S.pin[h] && cur(h) === S.pin[h]) c.push(h + ':' + S.pin[h]);
        else if (optional(h) && S.added[h] && cur(h)) c.push(h + ':' + cur(h));
      });
      if (c.length) o.push('c=' + c.map(encodeURIComponent).join(','));
      return o.join('&');
    }

    /* ---- actions ---- */
    // r20: the shopper's picks (chosen, have, added, own) carry over a setting change; setVolume (which every setting
    // change goes through) drops the ones the new setting / volume doesn't offer
    function setSetting(k) {
      if (!SET[k]) return;
      S.set = k; S.vol = null;
      applyDefaults();
    }
    // Still offered at station h: in its picks now, the locked product, or the product the shopper came from
    function offered(h, id) { return picks(h).indexOf(id) > -1 || id === lock(h) || (!!S.viewing[h] && id === S.pin[h]); }
    // Returns the choices dropped ([{ h, id }]) so the drawer can say so
    function setVolume(k) {
      var s = setting(); if (!s || !(s.volumes || []).some(function (v) { return v.key === k; })) return [];
      S.vol = k;
      var gone = [];
      // Station order: the machine and the grinder first, since other stations' picks follow them
      ST.forEach(function (st) {
        var h = st.h, c = S.chosen[h];
        if (c && !offered(h, c)) { delete S.chosen[h]; if (S.pin[h] === c) delete S.pin[h]; gone.push({ h: h, id: c }); }
        if (S.pin[h] && !offered(h, S.pin[h])) delete S.pin[h];
      });
      return gone;
    }
    function choose(h, id) {
      if (!STH[h] || !ok(id)) return;
      S.chosen[h] = id; S.have[h] = false;
      if (h === MACH) S.own = false;
      if (optional(h)) S.added[h] = true;
    }
    function haveOne(h) { if (h === MACH) S.own = true; else S.have[h] = true; }
    function addBack(h) { if (h === MACH) S.own = false; S.have[h] = false; }
    function remove(h) { S.added[h] = false; delete S.chosen[h]; delete S.pin[h]; }

    /* ---- start from the URL ---- */
    if (q.s && SET[q.s]) {
      setSetting(q.s);
      var sv = SET[q.s];
      if (q.v && (sv.volumes || []).some(function (v) { return v.key === q.v; })) S.vol = q.v;
      if (sv.askPower && q.p) S.power = String(q.p).toLowerCase();
      if (sv.askWater && q.w) S.water = String(q.w).toLowerCase();
    }
    if (q.m && MACH && ok(q.m)) S.lockM = q.m;
    if (q.g && GR && ok(q.g)) S.lockG = q.g;
    var fromPage = q.e === 'machine' || q.e === 'grinder';
    if (S.lockM) S.viewing[MACH] = true;
    if (S.lockG) S.viewing[GR] = true;
    String(q.c || '').split(',').forEach(function (pair) {
      var i = pair.indexOf(':'); if (i < 1) return;
      var h = pair.slice(0, i), id = pair.slice(i + 1);
      if (!STH[h]) return;
      if (id === '-') { haveOne(h); return; }
      if (!inStation(h, id) && !(h === MACH && ok(id))) return;
      S.pin[h] = id; S.chosen[h] = id; S.added[h] = true;
      if (fromPage) S.viewing[h] = true;
    });

    return {
      S: S, P: P, SET: SET, SETS: SETS, ST: ST, STH: STH, MACH: MACH, GR: GR,
      setting: setting, volume: volume, lock: lock, picks: picks, cur: cur, shown: shown, optional: optional, open: open,
      inBar: inBar, order: order, items: items, total: total, saving: saving, code: code, machineEmpty: machineEmpty,
      freight: freight, checkoutAdd: checkoutAdd, checkoutUrl: checkoutUrl, shareQuery: shareQuery,
      setSetting: setSetting, setVolume: setVolume, choose: choose, haveOne: haveOne, addBack: addBack, remove: remove
    };
  }

  if (typeof module !== 'undefined' && module.exports) { module.exports = { createModel: createModel, fill: fill }; }
  if (typeof document === 'undefined') return;

  /* ================= drawer ================= */
  var root = document.getElementById('bar-tool');
  if (!root) return;
  var PATH = root.getAttribute('data-path') || '';
  var QK = ['s', 'v', 'm', 'g', 'p', 'w', 'c', 'e', 'n'];
  var KEYS = ['bar'].concat(QK);
  // The header can be transformed (sticky header), which would pin a position:fixed child to it: move the shell to <body>
  if (root.parentNode !== document.body) document.body.appendChild(root);

  var IC = {};
  var tpl = document.getElementById('bar-ic');
  if (tpl) Array.prototype.forEach.call(tpl.content.querySelectorAll('[data-ic]'), function (n) { IC[n.getAttribute('data-ic')] = n.innerHTML; });
  function ic(n) { return IC[n] || ''; }

  /* ---- data + CSS, loaded once per page view ---- */
  var D = null, dataP = null, cssP = null;
  function loadData() {
    if (!dataP) {
      dataP = fetch(PATH + (PATH.indexOf('?') > -1 ? '&' : '?') + 'view=bar-data', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('bar-data ' + r.status); return r.text(); })
        .then(function (t) {
          // Plain JSON; tolerate markup a proxy may append (e.g. the theme dev reload script)
          try { D = JSON.parse(t); } catch (e) {
            var cut = t.search(/<script|<!--/i), j = cut > -1 ? t.slice(0, cut) : t;
            D = JSON.parse(j.slice(j.indexOf('{'), j.lastIndexOf('}') + 1));
          }
          D.products = D.products || {};
          return D;
        })
        .catch(function (e) { dataP = null; throw e; });
    }
    return dataP;
  }
  function loadCss() {
    if (!cssP) {
      cssP = new Promise(function (res) {
        var href = root.getAttribute('data-css'); if (!href) { res(); return; }
        var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href;
        l.onload = l.onerror = function () { res(); };
        document.head.appendChild(l);
      });
    }
    return cssP;
  }
  function preload() { if (dataP && cssP) return; loadCss(); loadData().catch(function () {}); }

  function toolUrl(a) {
    if (!PATH || !a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return null;
    var u; try { u = new URL(a.getAttribute('href'), location.href); } catch (e) { return null; }
    return u.origin === location.origin && u.pathname.slice(-PATH.length) === PATH ? u : null;
  }
  function queryOf(sp) { var q = {}; QK.forEach(function (k) { if (sp.get(k)) q[k] = sp.get(k); }); return q; }

  // Product-page cards carry their product in the bar-data shape: a machine / grinder in no station still locks with its data
  function mergePageProducts() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bar-product]'), function (el) {
      var o; try { o = JSON.parse(el.getAttribute('data-bar-product')); } catch (e) { return; }
      if (o && o.h && !D.products[o.h]) D.products[o.h] = o;
    });
  }

  var M = null, S = null, P = {}, T = {}, L = {}, CH = {};
  var app, mbar, layer, titleEl, ctlEl, bodyEl;
  var isOpen = false, opener = null, closeT;

  // A locked machine / grinder that neither the stations nor the page's cards list: /products/<handle>.js (no metafields)
  function ensure(handle) {
    if (!handle || P[handle]) return Promise.resolve();
    return fetch((D.root || '/').replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return;
        var v = (j.variants || []).filter(function (x) { return x.available; })[0] || (j.variants || [])[0] || {};
        var img = j.featured_image ? (j.featured_image.indexOf('//') === 0 ? 'https:' + j.featured_image : j.featured_image) : null;
        var sized = function (w) { return img ? img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : null; };
        P[handle] = { t: j.title, n: j.title, u: j.url || ('/products/' + handle), v: v.id, p: v.price, a: !!j.available, img: sized(400), img2: sized(800), w: 0, hh: 0, why: '', hr: 0, type: j.type, br: j.vendor, inst: false, gc: null, vc: null, ws: '', specs: [], ship: {} };
      })
      .catch(function () {});
  }

  /* ---- formatting ---- */
  var fmt0, fmt2;
  function initFmt() {
    if (fmt0) return;
    try {
      fmt0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: D.currency || 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 });
      fmt2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: D.currency || 'USD' });
    } catch (e) { fmt0 = fmt2 = { format: function (n) { return '$' + n; } }; }
  }
  function money(cents) { var n = (Number(cents) || 0) / 100; return n % 1 ? fmt2.format(n) : fmt0.format(n); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function short(h) { return h && P[h] ? (P[h].n || P[h].t) : ''; }
  function stLabel(h) { return (M.STH[h] && M.STH[h].label) || ''; }
  function volLabel() { var v = M.volume(); return v ? v.label : ''; }
  // "{volume}" for café / cart reads "<label> a day"; home volumes are people, not drinks
  function volText() { var l = volLabel(); return S.set === 'home' ? l : fill(T.per_day, { volume: l }); }
  // Inside a sentence (reason badges, sheet subtitle, warning) a label starting with a capitalised word reads lowercase:
  // "Best fit for under 100 a day". Tiles and chips keep the capital.
  function volIn() { var t = volText(); return /^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t; }
  // Badges and the verdict: "150–350/day" for café / cart, the label alone for home; lowercase like volIn
  function volShort() {
    var l = volLabel(), t = S.set === 'home' ? l : (T.per_day_short ? fill(T.per_day_short, { volume: l }) : volText());
    return /^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t;
  }
  function setNoun() { var s = M.setting(); return s ? String(s.label || '').toLowerCase() : ''; }
  // bar_tool.labels first (data), the locale string of the same word as fallback
  function lab(k) { return L[k] || T[k] || ''; }
  function note(h) { var s = M.setting(); return s && s.notes ? s.notes[h] : null; }

  /* ---- copy per piece ---- */
  function reason(h, id) {
    var n = note(h), r = (n && typeof n === 'object' && n.reason) || (M.STH[h] && M.STH[h].reason) || '';
    var need = { machine: /\{machine\}/.test(r), grinder: /\{grinder\}/.test(r) };
    var m = M.cur(M.MACH), g = M.cur(M.GR);
    if ((need.machine && !m) || (need.grinder && !g)) return L.our_pick || '';
    return fill(r, { volume: volShort(), machine: short(m), grinder: short(g) }) || L.our_pick || '';
  }
  /* ---- "built around your machine": an opening with a locked product (m / g). BI.fresh until the shopper's first
     change (verdict reason = labels.verdict_matched); BI.touched[h] (a change at that station) or BI.all (a setting /
     volume / power / water change) ends a tile's "Matched for you" badge; BI.play = the build-in still to run (the
     first bar render of the opening). Reset by openBar. ---- */
  var BI = { play: false, fresh: false, all: false, touched: {} };
  function hasLock() { return !!(S.lockM || S.lockG); }
  function lockedSt(h) { return (h === M.MACH && !!S.lockM) || (h === M.GR && !!S.lockG); }
  // h: the station changed; none = a change to the whole bar
  function changed(h) { BI.fresh = false; BI.play = false; if (h) BI.touched[h] = true; else BI.all = true; }

  // The piece the shopper came from reads "You were viewing", even when it is also our pick; with a locked product the
  // pieces we matched to it read "Matched for you" until the shopper changes them
  // r20: anything the shopper chose keeps "Your pick" across recomputes; a machine that is small or misfits for the
  // volume / setting never gets the "Best for {volume}" reason
  function badge(h, id) {
    var yours = { t: L.your_choice || T.your_choice, mine: true };
    if (S.viewing[h] && (id === M.lock(h) || id === S.pin[h])) return { t: lab('viewing'), mine: false };
    if (S.chosen[h] === id && id !== M.lock(h)) return yours;
    if (h === M.MACH && (small(id) || misfit(id))) return yours;
    if (hasLock() && L.added_badge && !lockedSt(h) && !BI.all && !BI.touched[h] && id !== S.pin[h]) return { t: L.added_badge, mine: false };
    if (id === M.picks(h)[1]) return { t: reason(h, id), mine: false };
    return yours;
  }
  // The volume's default machine: our pick, else the first one listed
  function bestMachine() { var pk = M.picks(M.MACH); return pk[1] || pk[0] || pk[2] || null; }
  // A machine (locked, linked or picked) smaller than every machine we'd pick for this volume (the r1 warning condition)
  function small(id) {
    var h = M.MACH;
    if (!id || !P[id] || M.picks(h).indexOf(id) > -1 || !P[id].gc) return false;
    var gcs = M.picks(h).filter(Boolean).map(function (x) { return P[x].gc; }).filter(Boolean);
    return !!gcs.length && P[id].gc < Math.min.apply(null, gcs);
  }
  // A machine that doesn't suit the setting (setting defaults.fit; none = anything fits): 'volt' when none of its
  // voltage classes is in fit.voltage, else 'groups' when it has more groups than fit.max_groups. The setting's own
  // machine picks (any volume / 110v key) never misfit.
  function misfit(id) {
    var s = M.setting(), fit = s && s.defaults && s.defaults.fit, p = id && P[id];
    if (!fit || !p) return false;
    var ps = (M.STH[M.MACH] && M.STH[M.MACH].picks && M.STH[M.MACH].picks[S.set]) || {};
    for (var k in ps) if (Array.isArray(ps[k]) && ps[k].indexOf(id) > -1) return false;
    var fv = fit.voltage || [];
    if (Array.isArray(p.vc) && p.vc.length && fv.length && !p.vc.some(function (x) { return fv.indexOf(x) > -1; })) return 'volt';
    if (p.gc && fit.max_groups && p.gc > fit.max_groups) return 'groups';
    return false;
  }
  // r20: water from tanks and a machine whose raw water source (product "ws") has no tank option: labels.tank_note on
  // the machine card and as the ready verdict's reason (not a warning)
  function tanks() { return /^tank/.test(String(S.water || '')); }
  function tankNote(id) {
    var ws = id && P[id] && P[id].ws;
    ws = Array.isArray(ws) ? ws.join(' ') : String(ws || '');
    if (!L.tank_note || !tanks() || !ws || /tank/i.test(ws)) return '';
    return fill(L.tank_note, { machine: short(id), volume: volShort() });
  }

  /* ---- verdict: a specialist's check, first matching rule wins (no machine → misfit machine → small machine → missing
     essential → ready). k = the state (is-<k>); act = [data attribute, value] of the action button; next = the first
     empty optional station ("Next we'd add", ready only). fits (ready): the machine is the volume's default pick or has
     its group count. ---- */
  function verdictData() {
    var o = verdictRule();
    // Opened with a locked product and nothing changed yet: the title (labels.verdict_matched_title, if set) and reason
    // say what we matched to it, only when the machine fits the volume. Warnings (misfit, small, missing) and "no
    // machine" (e.g. a grinder entry with the machine empty) keep their own title and reason.
    if (BI.fresh && hasLock() && L.verdict_matched && o.k === 'ready' && o.fits) {
      var n = M.items().filter(function (x) { return !lockedSt(x.h); }).length, lm = short(S.lockM || S.lockG);
      if (L.verdict_matched_title) o.title = fill(L.verdict_matched_title, { machine: lm, volume: volShort() });
      // The tank note (r20) stays the reason when it applies
      if (!o.tank) { o.text = fill(L.verdict_matched, { n: n, machine: lm, volume: volShort() }); o.matched = true; }
    }
    return o;
  }
  function verdictRule() {
    var mid = M.inBar(M.MACH) ? M.cur(M.MACH) : null, gid = M.cur(M.GR), v = M.volume() || {}, vd = v.verdict || {};
    var vars = { volume: volShort(), machine: short(mid), grinder: short(gid) };
    var filled = M.shown().filter(function (h) { return (v.filled || []).indexOf(h) > -1; });
    // A station the shopper already has ("I already have one") is covered
    var missing = filled.filter(function (h) { return !M.cur(h) && !S.have[h]; });
    if (!mid && !S.own) return { k: 'open', ic: 'tool', title: L.verdict_no_machine, text: L.verdict_no_machine_sub, act: ['choose', M.MACH], actT: T.choose_machine };
    var mf = misfit(mid);
    if (mf) {
      var mv = { machine: short(mid), groups: P[mid].gc, setting_noun: (CH.setting_noun || {})[S.set] || setNoun() };
      return { k: 'small', ic: 'excl', title: fill(L.verdict_misfit, mv), text: fill(mf === 'volt' ? L.verdict_misfit_volt : L.verdict_misfit_groups, mv), act: ['swap', M.MACH], actT: T.change_machine };
    }
    var best = bestMachine();
    vars.to = short(best);
    // Too small for the volume: name the volume's default machine and switch to it in one tap
    if (small(mid)) {
      var sw = best && best !== mid && L.verdict_small_act;
      return { k: 'small', ic: 'excl', title: fill(L.verdict_small, vars), text: fill(L.verdict_small_sub, vars), act: sw ? ['mswap', best] : ['swap', M.MACH], actT: sw ? fill(L.verdict_small_act, vars) : T.change_machine };
    }
    if (missing.length) {
      var h = missing[0];
      vars.station = stLabel(h).toLowerCase();
      return { k: 'missing', ic: 'excl', title: fill(L.verdict_missing, vars), text: (M.STH[h] && M.STH[h].note) || '', act: ['swap', h], actT: lab('add_it') };
    }
    // The volume's own title only for its default machine (or one with the same group count); otherwise the custom
    // title and text. With a fitting machine: our grinder too → the volume's reason; otherwise the pair by name.
    var fits = !mid || !best || mid === best || (!!P[mid].gc && P[mid].gc === P[best].gc);
    var ours = mid === best && gid === M.picks(M.GR)[1];
    var o = fits
      ? { k: 'ready', ic: 'check', fits: true, title: fill(vd.title, vars), text: fill(ours || !mid || !gid ? vd.text : L.verdict_custom_text, vars) }
      : { k: 'ready', ic: 'check', fits: false, title: fill(L.verdict_custom_title || vd.title, vars), text: fill(L.verdict_custom_text, vars) };
    var tn = tankNote(mid); if (tn) { o.text = tn; o.tank = true; }
    var nx = M.shown().filter(function (x) { return M.open(x); })[0];
    if (nx && L.verdict_next) o.next = { h: nx, t: fill(L.verdict_next, { station: stLabel(nx).toLowerCase() }) };
    return o;
  }
  // Light block: icon · "Verdict:" + title · action (small clay text button) on one line, the reason under it.
  // "Next we'd add" (ready) and the rule's action never come together. is-matched (the verdict_matched reason): phones
  // show that line instead of "Next we'd add" (.is-next).
  function verdict() {
    var o = verdictData();
    if (!o.title) return '';
    var b = o.next ? ['choose', o.next.h, o.next.t] : o.act ? [o.act[0], o.act[1], o.actT] : null;
    return '<div class="bt-verdict is-' + o.k + (o.matched ? ' is-matched' : '') + (o.tank ? ' is-tank' : '') + '"><span class="bt-verdict__ic">' + ic(o.ic) + '</span>' +
      '<p class="bt-verdict__h">' + (L.verdict_label ? '<b class="bt-verdict__l">' + esc(L.verdict_label) + '</b> ' : '') + esc(o.title) + '</p>' +
      (b ? '<button type="button" class="bt-verdict__act' + (o.next ? ' is-next' : '') + '" data-' + b[0] + '="' + esc(b[1]) + '">' + esc(b[2]) + ic('arrow') + '</button>' : '') +
      (o.text ? '<p class="bt-verdict__p">' + esc(o.text) + '</p>' : '') + '</div>';
  }
  function noteLine(h) { var n = note(h); return typeof n === 'string' ? n : (n && n.note) || ''; }
  // A label that is either one string or an object per setting
  function perSet(k) { var v = L[k]; return v && typeof v === 'object' ? v[S.set] || '' : v || ''; }

  /* ---- totals: one set for the plan, phone bar, chat card, summary and email. net = pieces − bar saving + freight;
     fr = freight in cents (0 = free, null = unknown: no row) ---- */
  function sums(list) {
    var c = M.code(list), tot = M.total(list), sv = c ? c.saving * 100 : 0, fr = M.freight(list);
    return { c: c, tot: tot, sv: sv, fr: fr, net: tot - sv + (fr || 0) };
  }
  function freightText(fr) { return fr == null ? '' : fr ? money(fr) : (L.freight_free || money(0)); }
  function why(id) { return (P[id] && P[id].why) || ''; }

  /* ---- arrival: the latest piece, from shipping tier ship_time / delivery_days and lead_time window_days (business days) ---- */
  function addBiz(d, n) { var x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); var w = x.getDay(); if (w && w < 6) n--; } return x; }
  function arrival(list) {
    var lo = 0, hi = 0;
    for (var i = 0; i < list.length; i++) {
      var s = P[list[i].id].ship || {}, del = Number(s.d) || 0, win = Number(s.w) || 0, st = nums(s.st);
      if (!del && !win) return '';
      var smax = st.length ? Math.max.apply(null, st) : 0, smin = st.length ? Math.min.apply(null, st) : 0;
      var span = win ? win + Math.max(0, del - smax) : del;
      var low = win ? span : Math.max(1, span - (smax - smin));
      if (span > hi) { hi = span; lo = low; }
    }
    if (!hi) return '';
    var t = new Date(), a = addBiz(t, lo), b = addBiz(t, hi);
    var mo = function (d) { return d.toLocaleDateString('en-US', { month: 'short' }); };
    if (a.getTime() === b.getTime()) return mo(b) + ' ' + b.getDate();
    if (a.getMonth() === b.getMonth()) return mo(a) + ' ' + a.getDate() + '–' + b.getDate();
    return mo(a) + ' ' + a.getDate() + ' – ' + mo(b) + ' ' + b.getDate();
  }
  function installLine() {
    var m = M.inBar(M.MACH) ? M.cur(M.MACH) : null;
    if (m && P[m].inst) { var n = short(m), br = P[m].br || ''; return fill(T.install_brand, { machine: n.indexOf(br) === 0 ? n : (br + ' ' + n).trim() }); }
    return T.install_remote;
  }

  /* ---- analytics ---- */
  function track(event, o) { if (window.dataLayer && typeof window.dataLayer.push === 'function') { o.event = event; window.dataLayer.push(o); } }

  /* ---- UI state ---- */
  // need: the setup screen ({ vol: the chosen volume or null }), null otherwise
  var U = { sheet: null, email: null, chat: null, toast: '', focus: null, back: null, need: null };
  var viewed = false;

  // Names on screen are the short name; the full title only goes in the image alt text
  function img(id, cls, eager) {
    var p = P[id]; if (!p || !p.img) return '';
    var dims = p.w && p.hh ? ' width="' + p.w + '" height="' + p.hh + '"' : '';
    return '<img class="' + cls + '" src="' + esc(p.img) + '" srcset="' + esc(p.img) + ' 400w, ' + esc(p.img2 || p.img) + ' 800w" sizes="' + (eager ? '(max-width:768px) 90vw, 520px' : '(max-width:768px) 45vw, 240px') + '"' + dims + ' alt="' + esc(p.t) + '" ' + (eager ? 'loading="eager" fetchpriority="high"' : 'loading="lazy" decoding="async"') + '>';
  }

  function settingTiles() {
    return '<ul class="bt-qtiles" role="list">' + M.SETS.map(function (s) {
      return '<li><button type="button" class="bt-qt" data-set="' + esc(s.key) + '"><span class="bt-qt__ic">' + ic(s.icon) + '</span><b>' + esc(s.label) + '</b>' + (s.sub ? '<small>' + esc(s.sub) + '</small>' : '') + '<span class="bt-qt__go">' + ic('arrow') + '</span></button></li>';
    }).join('') + '</ul>';
  }
  // The setup screen's volume tiles: each goes straight to the bar for that volume
  function volumeTiles() {
    var s = M.setting();
    return '<ul class="bt-qtiles' + ((s.volumes || []).length === 2 ? ' bt-qtiles--2' : '') + '" role="list">' + (s.volumes || []).map(function (v) {
      return '<li><button type="button" class="bt-qt" data-svol="' + esc(v.key) + '"><span class="bt-qt__cups">' + ic('cups' + Math.max(1, Math.min(4, Number(v.cups) || 1))) + '</span><b>' + esc(v.label) + '</b>' + (v.sub ? '<small>' + esc(v.sub) + '</small>' : '') + '<span class="bt-qt__go">' + ic('arrow') + '</span></button></li>';
    }).join('') + '</ul>';
  }
  function powerLabel(pw) { return pw === '110v' ? T.power_110 : T.power_240; }
  function waterLabel(wt) { return wt === 'plumbed' ? T.water_plumbed : T.water_tank; }
  // Power / water pills for setting s with the given answers (the current ones by default)
  function facts(s, pw, wt) {
    s = s || M.setting();
    if (arguments.length < 2) { pw = S.power; wt = S.water; }
    var h = '';
    if (pw) h += s.askPower ? '<button type="button" class="bt-fact" data-toggle="power" aria-label="' + esc(T.power_change) + '"><b>' + esc(powerLabel(pw)) + '</b><span class="bt-fact__ed">' + esc(L.change || '') + '</span></button>' : '<span class="bt-fact is-static"><b>' + esc(powerLabel(pw)) + '</b></span>';
    if (wt) h += s.askWater ? '<button type="button" class="bt-fact" data-toggle="water" aria-label="' + esc(T.water_change) + '"><b>' + esc(waterLabel(wt)) + '</b><span class="bt-fact__ed">' + esc(L.change || '') + '</span></button>' : '<span class="bt-fact is-static"><b>' + esc(waterLabel(wt)) + '</b></span>';
    return h;
  }

  // Body question when no setting is known: the setting tiles, centred in the drawer
  function stepView() {
    return '<section class="bt-step bt-step--set" aria-labelledby="bt-q1"><p class="bt-q" id="bt-q1">' + esc(D.question) + '</p>' + settingTiles() + '</section>';
  }

  /* ---- the setup screen (r16, r17, r21): shown only when no volume is known. Title, the setting's volume question
     (tiles that go straight to the bar) + power / water pills. U.need = { vol: null, or the volume from a saved history
     entry }; while it shows, S.vol is that volume or the setting's default volume. ---- */
  function dvol() {
    var s = M.setting() || {}, vs = s.volumes || [];
    return vs.some(function (v) { return v.key === s.dv; }) ? s.dv : (vs[0] ? vs[0].key : null);
  }
  function startSetup(sel) {
    var vs = (M.setting() || {}).volumes || [];
    if (!vs.some(function (v) { return v.key === sel; })) sel = null;
    U.need = { vol: sel };
    M.setVolume(sel || dvol());
  }
  function setupView() {
    var s = M.setting(), f = (s.askPower || s.askWater) ? facts() : '';
    var tt = L.setup_title, title = tt && typeof tt === 'object' ? tt[S.set] : tt;
    return '<section class="bt-step bt-setup" aria-labelledby="bt-su-t">' +
      '<div class="bt-setup__h"><h2 class="bt-setup__t" id="bt-su-t" tabindex="-1">' + esc(title || '') + '</h2>' + (L.setup_text ? '<p class="bt-q-sub">' + esc(L.setup_text) + '</p>' : '') + '</div>' +
      '<div class="bt-setup__s" role="group" aria-labelledby="bt-su-q"><p class="bt-setup__l" id="bt-su-q">' + esc(s.q || '') + '</p>' + volumeTiles() +
        (f ? '<div class="bt-facts">' + f + '</div>' : '') + '</div></section>';
  }
  // The setup screen as history state: its volume (Back to it)
  function needState() { return U.need ? { vol: U.need.vol } : null; }
  function saveNeed() {
    var st = Object.assign({}, history.state || {}), n = needState();
    if (n) st.need = n; else delete st.need;
    try { history.replaceState(st, ''); } catch (e) {}
  }
  // Our history entries: { bar: 1, d: how many of ours up to this one (Close steps back over all of them), need }
  function hpush() {
    var cur = history.state, st = { bar: 1, d: (isOpen && cur && cur.bar ? Number(cur.d) || 1 : 0) + 1 }, n = needState();
    if (n) st.need = n;
    try { history.pushState(st, '', barUrl()); } catch (e) {}
  }
  // A volume tile on the setup screen: that volume, then the bar with every station as the data gives it (the build-in
  // still runs)
  function needGo(k) {
    if (!U.need || !k) return;
    M.setVolume(k);
    track('bar_setup', { setting: S.set, volume: S.vol });
    U.need = null;
    // The setup on our own entry: the bar gets its own (Back returns to the setup); one opened by a page load: replaced
    if (history.state && history.state.bar) hpush();
    else saveNeed();
    U.focus = '[data-dr-close]';
    if (bodyEl) bodyEl.scrollTop = 0;
    render();
  }

  /* ---- top bar controls: setting segmented control · volume group (label + segmented control) · facts group.
     The three groups keep their place and width whatever the setting or volume (widths measured once over every
     setting: --bt-volw / --bt-factw), so nothing in the bar moves; each segmented control has one sliding pill. ---- */
  var ctlFor = null, volFor = null, measured = false;
  function volButtons(s, sel) {
    return (s.volumes || []).map(function (v) { return '<button type="button" class="bt-vchip" data-vol="' + esc(v.key) + '" aria-pressed="' + (v.key === sel) + '">' + esc(v.label) + '</button>'; }).join('');
  }
  function volLabelFor(k) { return (L.volume_label || {})[k] || ''; }
  function renderCtl() {
    var s = M.setting();
    ctlEl.hidden = !s;
    if (!s) { ctlEl.innerHTML = ''; ctlFor = null; return; }
    if (ctlFor !== M) {
      ctlEl.innerHTML = '<div class="bt-seg" data-seg role="group" aria-label="' + esc(D.question) + '"><span class="bt-pill" aria-hidden="true"></span>' +
        M.SETS.map(function (x) { return '<button type="button" class="bt-seg__b" data-set="' + esc(x.key) + '" aria-pressed="false">' + ic(x.icon) + '<span>' + esc(x.label) + '</span></button>'; }).join('') + '</div>' +
        '<div class="bt-volg"><span class="bt-volg__l"></span><div class="bt-vseg" data-seg role="group"><span class="bt-pill" aria-hidden="true"></span></div></div>' +
        '<div class="bt-factg" role="group" aria-label="' + esc(T.answers) + '"></div>';
      ctlFor = M; volFor = null;
    }
    Array.prototype.forEach.call(ctlEl.querySelectorAll('.bt-seg__b'), function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-set') === S.set)); });
    // Volume group: empty (space kept) until a volume is chosen; buttons rebuilt only when the setting changes
    var vseg = ctlEl.querySelector('.bt-vseg'), key = S.vol && !U.need ? S.set : '';
    if (volFor !== key) {
      Array.prototype.forEach.call(vseg.querySelectorAll('.bt-vchip'), function (b) { b.parentNode.removeChild(b); });
      if (key) vseg.insertAdjacentHTML('beforeend', volButtons(s, S.vol));
      vseg.setAttribute('aria-label', key ? (s.q || '') : '');
      ctlEl.querySelector('.bt-volg__l').textContent = key ? volLabelFor(S.set) : '';
      ctlEl.querySelector('.bt-volg').classList.toggle('is-empty', !key);
      volFor = key;
    }
    Array.prototype.forEach.call(vseg.querySelectorAll('.bt-vchip'), function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-vol') === S.vol)); });
    ctlEl.querySelector('.bt-factg').innerHTML = S.vol && !U.need ? facts() : '';
    if (!measured) measureCtl();
    pills();
  }
  // Each pill slides (transform / width, 200ms in CSS) to its group's pressed button; first placement doesn't animate
  function pills() {
    Array.prototype.forEach.call(ctlEl.querySelectorAll('[data-seg]'), function (seg) {
      var pill = seg.querySelector('.bt-pill'), b = seg.querySelector('[aria-pressed="true"]');
      if (!pill) return;
      if (!b) { pill.style.opacity = '0'; pill.removeAttribute('data-on'); return; }
      var first = !pill.hasAttribute('data-on');
      if (first) pill.classList.add('no-anim');
      pill.style.width = b.offsetWidth + 'px';
      pill.style.height = b.offsetHeight + 'px';
      pill.style.transform = 'translate(' + b.offsetLeft + 'px,' + b.offsetTop + 'px)';
      pill.style.opacity = '1';
      pill.setAttribute('data-on', '');
      if (first) { void pill.offsetWidth; pill.classList.remove('no-anim'); }
    });
    // Phones: the controls row scrolls; keep the selected volume in view
    var sel = ctlEl.querySelector('.bt-vseg [aria-pressed="true"]');
    if (sel && ctlEl.scrollWidth > ctlEl.clientWidth) {
      var r = sel.getBoundingClientRect(), c = ctlEl.getBoundingClientRect();
      if (r.left < c.left + 16) ctlEl.scrollLeft -= c.left + 16 - r.left;
      else if (r.right > c.right - 16) ctlEl.scrollLeft += r.right - c.right + 16;
    }
  }
  // Fixed widths = the widest case over every setting (volume group) and every power / water answer (facts group)
  function measureCtl() {
    if (!ctlEl.offsetParent) return;
    var box = document.createElement('div'), html = '';
    box.className = 'bt-measure';
    M.SETS.forEach(function (s) {
      html += '<div class="bt-volg is-nat"><span class="bt-volg__l">' + esc(volLabelFor(s.key)) + '</span><div class="bt-vseg">' + volButtons(s, null) + '</div></div>';
      var d = s.defaults || {}, pws = s.askPower ? ['240v', '110v'] : [String(d.power || '').toLowerCase()], wts = s.askWater ? ['plumbed', 'tanks'] : [String(d.water || '').toLowerCase()];
      pws.forEach(function (pw) { wts.forEach(function (wt) { html += '<div class="bt-factg is-nat">' + facts(s, pw, wt) + '</div>'; }); });
    });
    box.innerHTML = html;
    ctlEl.appendChild(box);
    var mx = function (sel) { return Array.prototype.reduce.call(box.querySelectorAll(sel), function (a, n) { return Math.max(a, Math.ceil(n.getBoundingClientRect().width)); }, 0); };
    var vw = mx('.bt-volg'), fw = mx('.bt-factg');
    ctlEl.removeChild(box);
    if (vw) root.style.setProperty('--bt-volw', vw + 'px');
    root.style.setProperty('--bt-factw', fw + 'px');
    measured = !!vw;
  }

  function machineCard() {
    var h = M.MACH, id = M.cur(h), lab = esc(stLabel(h));
    if (S.own) {
      return '<article class="bt-hero is-own"><div class="bt-hero__b"><span class="bt-why is-mine">' + esc(T.own_machine_badge) + '</span><h3>' + esc(T.own_machine_title) + '</h3><p class="bt-t-why">' + esc(T.own_machine_text) + '</p>' +
        '<div class="bt-hero__buy"><span></span><button type="button" class="bt-btn-o" data-choose="' + esc(h) + '">' + esc(T.add_machine) + '</button></div></div></article>';
    }
    if (M.machineEmpty() || !id) {
      return '<article class="bt-hero"><button type="button" class="bt-hero__st is-empty" data-choose="' + esc(h) + '" aria-label="' + esc(T.choose_machine_aria) + '"><span class="bt-st-name">' + lab + '</span><span class="bt-plus is-big" aria-hidden="true">+</span></button>' +
        '<div class="bt-hero__b"><h3>' + esc(perSet('machine_empty_title') || T.machine_empty_title) + '</h3><p class="bt-t-why">' + esc(T.machine_empty_text) + '</p>' +
        '<div class="bt-hero__buy is-two"><button type="button" class="bt-btn" data-choose="' + esc(h) + '">' + esc(T.choose_machine) + '</button><button type="button" class="bt-btn-o" data-have="' + esc(h) + '">' + esc(T.already_have) + '</button></div></div></article>';
    }
    // The setting's machine note (e.g. the cart's 110V line) is wrong for a machine that misfits: hidden. Water from
    // tanks and a plumbed-only machine: the tank note in its place
    var b = badge(h, id), p = P[id], nl = tankNote(id) || (misfit(id) ? '' : noteLine(h));
    var chips = (p.specs || []).map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('') + (p.inst ? '<span class="is-acc">' + esc(T.install_chip) + '</span>' : '');
    return '<article class="bt-hero"><div class="bt-hero__st"><span class="bt-st-name">' + lab + '</span>' + img(id, 'bt-hero__img', true) + '</div>' +
      '<div class="bt-hero__b">' + (b.t ? '<span class="bt-why' + (b.mine ? ' is-mine' : '') + '">' + esc(b.t) + '</span>' : '') +
      '<h3><a href="' + esc(p.u) + '">' + esc(short(id)) + '</a></h3>' + (why(id) ? '<p class="bt-t-why">' + esc(why(id)) + '</p>' : '') +
      (chips ? '<div class="bt-specs">' + chips + '</div>' : '') +
      (nl ? '<p class="bt-t-note">' + esc(nl) + '</p>' : '') +
      '<div class="bt-hero__buy"><b class="bt-hero__price">' + money(p.p) + '</b><button type="button" class="bt-btn-o" data-swap="' + esc(h) + '">' + esc(T.change_machine) + '</button></div></div></article>';
  }

  // r25 "What to add next": an open station as a compact card ("+", station, its nudge, "Choose one →"), no image area
  function addCard(h) {
    var lab = stLabel(h), st = M.STH[h] || {};
    return '<li><button type="button" class="bt-t is-empty bt-t--add" data-st="' + esc(h) + '" data-choose="' + esc(h) + '" aria-label="' + esc(fill(T.choose_aria, { station: lab })) + '"><span class="bt-plus" aria-hidden="true">+</span>' +
      '<span class="bt-t__b"><b class="bt-t__n">' + esc(lab) + '</b>' + (st.nudge ? '<span class="bt-t__why">' + esc(st.nudge) + '</span>' : '') + '<span class="bt-t__go">' + esc(T.choose_one) + '</span></span></button></li>';
  }

  // A row's heading: labels title + muted sub (either missing: that text is left out)
  function rowHead(t, sub) {
    if (!t && !sub) return '';
    return '<h2 class="bt-rowhd">' + (t ? '<span>' + esc(t) + '</span>' : '') + (sub ? '<small>' + esc(sub) + '</small>' : '') + '</h2>';
  }

  // r25: the fitted row (filled and "using my own" tiles) and the add row (open stations), in bar order
  function rows() {
    var all = M.shown(), fit = all.filter(function (h) { return !M.open(h); }), add = all.filter(M.open);
    var mid = M.inBar(M.MACH) && !M.machineEmpty() ? M.cur(M.MACH) : null;
    return (fit.length ? '<div class="bt-row">' + rowHead(L.row_fitted_title, mid && L.row_fitted_sub ? fill(L.row_fitted_sub, { machine: short(mid) }) : '') +
        '<ul class="bt-grid" role="list" style="--cols:' + cols(fit.length) + '">' + fit.map(tile).join('') + '</ul></div>' : '') +
      (add.length ? '<div class="bt-row">' + rowHead(L.row_add_title, perSet('row_add_sub')) +
        '<ul class="bt-grid bt-grid--add" role="list">' + add.map(addCard).join('') + '</ul></div>' : '');
  }

  function tile(h) {
    var lab = stLabel(h);
    var id = M.cur(h);
    if (!id) {
      return '<li><article class="bt-t is-have" data-st="' + esc(h) + '"><div class="bt-t__st"><span class="bt-st-name">' + esc(lab) + '</span><span class="bt-t__own">' + esc(T.using_own) + '</span></div><div class="bt-t__b"><b class="bt-t__n">' + esc(T.left_out) + '</b>' +
        '<div class="bt-t__foot"><b class="bt-t__p">' + money(0) + '</b><button type="button" class="bt-t__btn" data-back="' + esc(h) + '">' + esc(T.add_back) + '</button></div></div></article></li>';
    }
    var b = badge(h, id), p = P[id], nl = noteLine(h);
    return '<li><article class="bt-t" data-st="' + esc(h) + '"><div class="bt-t__st"><span class="bt-st-name">' + esc(lab) + '</span>' + img(id, 'bt-t__img', false) + '</div>' +
      '<div class="bt-t__b">' + (b.t ? '<span class="bt-why' + (b.mine ? ' is-mine' : '') + '">' + esc(b.t) + '</span>' : '') +
      '<b class="bt-t__n"><a href="' + esc(p.u) + '">' + esc(short(id)) + '</a></b>' + (why(id) ? '<span class="bt-t__why">' + esc(why(id)) + '</span>' : '') +
      (nl ? '<span class="bt-t-note">' + esc(nl) + '</span>' : '') +
      '<div class="bt-t__foot"><b class="bt-t__p">' + money(p.p) + '</b><button type="button" class="bt-t__btn" data-swap="' + esc(h) + '">' + esc(L.change || '') + '</button></div>' +
      (M.optional(h) ? '<button type="button" class="bt-t__rm" data-remove="' + esc(h) + '">' + esc(L.remove || '') + '</button>' : '') + '</div></article></li>';
  }

  function cols(n) { return n <= 1 ? 2 : n <= 4 ? n : n <= 6 ? 3 : 4; }

  function planPanel(list) {
    var s = sums(list), c = s.c, arr = arrival(list);
    var hs = M.order().filter(function (h) { return !M.open(h) && !(h === M.MACH && M.machineEmpty()); });
    var row = function (h) {
      var id = M.cur(h);
      return id ? '<li data-st="' + esc(h) + '"><span>' + esc(short(id)) + '</span><span>' + money(P[id].p) + '</span></li>' : '<li class="is-muted" data-st="' + esc(h) + '"><span>' + esc(fill(T.plan_own, { station: stLabel(h) })) + '</span><span>' + money(0) + '</span></li>';
    };
    var ul = function (x) { return '<ul class="bt-lines" role="list">' + x.map(row).join('') + '</ul>'; };
    // With a locked product: "You picked" (the locked row) and "Matched to it" (the rest, bar order)
    var group = function (k, t, x) { return x.length ? '<div class="bt-lgroup" data-g="' + k + '">' + (t ? '<h3 class="bt-lgroup__h">' + esc(t) + '</h3>' : '') + ul(x) + '</div>' : ''; };
    var lines = hasLock()
      ? '<div class="bt-lgroups">' + group('yours', L.plan_yours, hs.filter(lockedSt)) + group('matched', L.plan_matched, hs.filter(function (h) { return !lockedSt(h); })) + '</div>'
      : ul(hs);
    var net = s.net;
    return '<aside class="bt-plan" aria-label="' + esc(T.plan_title) + '"><h2 class="bt-plan__h">' + esc(T.plan_title) + '</h2>' +
      lines +
      (s.fr != null ? '<div class="bt-fr"><span>' + esc(L.freight || '') + '</span><span>' + esc(freightText(s.fr)) + '</span></div>' : '') +
      (c ? '<div class="bt-saving"><span>' + esc(L.saving || '') + '</span><b>−' + money(c.saving * 100) + '</b></div>' : '') +
      '<div class="bt-total"><span>' + esc(fill(T.pieces, { count: list.length })) + '</span><b data-total>' + money(net) + '</b></div>' +
      '<div class="bt-promise">' + (arr ? '<div>' + ic('truck') + '<span>' + esc(fill(T.arrives, { date: '' })).replace(/\s*$/, ' ') + '<b>' + esc(arr) + '</b></span></div>' : '') +
      '<div>' + ic('wrench') + '<span>' + esc(installLine()) + '</span></div></div>' +
      '<button type="button" class="bt-btn bt-btn--lg" data-cart' + (list.length ? '' : ' disabled') + '>' + ic('lock') + esc(L.checkout || '') + ' · <span data-total>' + money(net) + '</span></button>' +
      '<button type="button" class="bt-btn2" data-chat><span class="bt-live" aria-hidden="true"></span>' + esc(L.talk || '') + '</button>' +
      '<p class="bt-chat-note">' + esc(T.chat_note) + '</p>' +
      '<div class="bt-links"><button type="button" class="bt-lnk" data-email>' + esc(T.email_plan) + '</button><button type="button" class="bt-lnk" data-copy>' + esc(T.copy_link) + '</button></div>' +
      '<p class="bt-trust">' + ic('shield') + ' ' + esc(T.trust) + '</p></aside>';
  }

  // Option tags per product, from the picks shown: the default (index 1, else the piece cur() falls back to) is "Best
  // overall"; of the others, the cheapest below it "Best value", the dearest above it "Top choice", the rest "Most
  // popular". { t: tag per index, def: the default's index }
  function tags(pk) {
    var def = [1, 0, 2].filter(function (i) { return pk[i]; })[0], t = [];
    if (def == null) return { t: t, def: -1 };
    var pr = function (i) { return Number(P[pk[i]].p) || 0; }, dp = pr(def);
    var rest = [0, 1, 2].filter(function (i) { return i !== def && pk[i]; });
    var lo = rest.filter(function (i) { return pr(i) < dp; }).sort(function (a, b) { return pr(a) - pr(b); })[0];
    var hi = rest.filter(function (i) { return pr(i) > dp; }).sort(function (a, b) { return pr(b) - pr(a); })[0];
    t[def] = L.best || L.our_pick;
    rest.forEach(function (i) { t[i] = i === lo ? L.tag_value : i === hi ? L.tag_top : L.tag_popular; });
    return { t: t, def: def };
  }

  function sheetHTML() {
    var h = U.sheet; if (!h) return '';
    var pk = M.picks(h), sel = M.open(h) ? null : M.cur(h);
    var tg = tags(pk);
    var opts = pk.map(function (id, i) {
      if (!id) return '';
      var p = P[id];
      return '<button type="button" class="bt-opt" data-pick="' + esc(id) + '" aria-pressed="' + (sel === id) + '"><span class="bt-opt__st">' + img(id, 'bt-opt__img', false) + '</span>' +
        '<span class="bt-opt__b"><span class="bt-tier' + (i === tg.def ? ' is-best' : '') + '">' + esc(tg.t[i] || '') + '</span><span class="bt-opt__n">' + esc(short(id)) + '</span>' + (p.why ? '<span class="bt-opt__w">' + esc(p.why) + '</span>' : '') +
        '<span class="bt-opt__p"><b>' + money(p.p) + '</b></span></span></button>';
    }).join('');
    var n = pk.filter(Boolean).length || 1;
    return '<div class="bt-scrim" data-close><div class="bt-sheet" role="dialog" aria-modal="true" aria-labelledby="bt-sh-t">' +
      '<div class="bt-sheet__h"><div><h3 id="bt-sh-t">' + esc(stLabel(h)) + '</h3><p>' + esc(fill(T.sheet_sub, { volume: volIn(), setting: setNoun() })) + '</p></div><button type="button" class="bt-x" data-close aria-label="' + esc(T.close) + '">×</button></div>' +
      '<div class="bt-opts" style="--n:' + n + '">' + opts + '</div>' +
      '<div class="bt-sheet__f"><button type="button" class="bt-lnk" data-have="' + esc(h) + '">' + esc(T.already_have) + '</button></div></div></div>';
  }

  // "Email me this plan": a small sheet in the chat sheet's style. U.email = { value, busy, err, done (the address sent to) }
  function emailHTML() {
    var e = U.email; if (!e) return '';
    var body = e.done
      ? '<p class="bt-ok" role="status" tabindex="-1">' + esc(fill(lab('email_done'), { email: e.done })) + '</p>'
      : '<form class="bt-field" data-form="email" novalidate><label for="bt-em">' + esc(T.your_email) + '</label><input id="bt-em" type="email" autocomplete="email" placeholder="' + esc(T.email_placeholder) + '" value="' + esc(e.value || '') + '" required>' +
        '<p class="bt-err" role="alert"' + (e.err ? '' : ' hidden') + '>' + esc(T.email_error) + '</p>' +
        '<button class="bt-btn" type="submit"' + (e.busy ? ' disabled' : '') + '>' + esc(T.email_send) + '</button></form>';
    return '<div class="bt-cc-wrap" role="dialog" aria-modal="false" aria-labelledby="bt-em-t"><div class="bt-cc bt-cc--em">' +
      '<div class="bt-cc-h"><div><b id="bt-em-t">' + esc(lab('email_title')) + '</b></div><button type="button" class="bt-x" data-eclose aria-label="' + esc(T.close) + '">×</button></div>' +
      '<div class="bt-em">' + (lab('email_text') ? '<p class="bt-em__t">' + esc(lab('email_text')) + '</p>' : '') + body + '</div></div></div>';
  }

  function chatQuestions() {
    var noun = (CH.setting_noun || {})[S.set] || setNoun();
    return (CH.questions || []).map(function (x) { return fill(x, { setting_noun: noun }); });
  }
  function chatHTML() {
    var c = U.chat; if (!c) return '';
    var list = M.items(), qs = chatQuestions(), s = sums(list);
    // The plan's total (after the bar saving, with freight) and its saving line
    var card = '<div class="bt-cc-card"><div class="bt-cc-card__h"><b>' + esc(fill(T.your_bar, { setting: setNoun() })) + '</b><span>' + esc(volText()) + '</span></div><ul role="list">' +
      list.map(function (x) { return '<li><span>' + esc(short(x.id)) + '</span><span>' + money(P[x.id].p) + '</span></li>'; }).join('') +
      (s.fr != null ? '<li><span>' + esc(L.freight || '') + '</span><span>' + esc(freightText(s.fr)) + '</span></li>' : '') +
      '</ul>' + (s.sv ? '<div class="bt-cc-card__s"><span>' + esc(L.saving || '') + '</span><b>−' + money(s.sv) + '</b></div>' : '') +
      '<div class="bt-cc-card__t"><span>' + esc(fill(T.pieces, { count: list.length })) + '</span><b>' + money(s.net) + '</b></div></div>';
    var body = '<div class="bt-cc-sys">' + esc(T.chat_sys) + '</div>' + card;
    if (c.step === 1) {
      body += '<p class="bt-cc-ask">' + esc(T.chat_ask) + '</p><div class="bt-cc-chips">' + qs.map(function (x, i) {
        return i === qs.length - 1 && qs.length > 2 ? '<button type="button" class="bt-cc-chip is-own" data-cown>' + esc(x) + '</button>' : '<button type="button" class="bt-cc-chip" data-cq="' + i + '">' + esc(x) + '</button>';
      }).join('') + '</div>';
    } else {
      body += '<div class="bt-cc-me">' + esc(c.q) + '</div><div class="bt-cc-them">' + esc(CH.email_prompt || '') + '</div>' +
        '<form class="bt-cc-form" data-form="chat-email"><label for="bt-cc-em" class="bt-vh">' + esc(T.your_email) + '</label><input id="bt-cc-em" type="email" autocomplete="email" placeholder="' + esc(T.email_placeholder) + '" value="' + esc(knownEmail()) + '" required><button type="submit">' + esc(T.chat_send) + '</button></form>';
    }
    return '<div class="bt-cc-wrap" role="dialog" aria-modal="false" aria-label="' + esc(T.chat_label) + '"><div class="bt-cc">' +
      '<div class="bt-cc-h"><span class="bt-cc-av" aria-hidden="true">' + esc(String(T.chat_title || '').charAt(0)) + '</span><div><b>' + esc(T.chat_title) + '</b></div><button type="button" class="bt-x" data-cclose aria-label="' + esc(T.close) + '">×</button></div>' +
      '<div class="bt-cc-body" data-cc-body>' + body + '</div>' +
      (c.step === 1 ? '<form class="bt-cc-input" data-form="chat-q"><label for="bt-cc-q" class="bt-vh">' + esc(T.your_question) + '</label><input id="bt-cc-q" type="text" autocomplete="off" placeholder="' + esc(T.chat_own_placeholder) + '"><button type="submit" aria-label="' + esc(T.chat_send) + '">➤</button></form>' : '') +
      '</div></div>';
  }

  function render() {
    if (!M || !isOpen) return;
    // Top bar: setting-aware title + compact controls (none until a setting is known)
    // A setting with no finished setup is always on the setup screen
    if (S.set && !S.vol && !U.need) startSetup(null);
    titleEl.textContent = S.set ? fill(T.your_bar, { setting: setNoun() }) : (D.page_title || D.name || '');
    renderCtl();
    syncUrl();

    var step = !S.set || !S.vol || !!U.need;
    bodyEl.classList.toggle('is-q', step);
    if (step) {
      app.innerHTML = S.set ? setupView() : stepView();
      mbar.hidden = true; mbar.innerHTML = '';
      layer.innerHTML = '';
      if (U.focus) { var f0 = root.querySelector(U.focus); if (f0) f0.focus(); U.focus = null; }
      return;
    }
    var list = M.items();
    app.innerHTML =
      '<div class="bt-main"><div class="bt-col">' + verdict() + machineCard() + rows() +
      '</div>' + planPanel(list) + '</div>';
    skelApply();
    if (U.flash) { flash(app.querySelector('.bt-t[data-st="' + U.flash + '"]')); U.flash = null; }

    var sm = sums(list), net = sm.net, arr = arrival(list);
    // Phone bar: a thin row (count · arrival when it fits · "Save $850" when there is a saving · total) above the two buttons
    mbar.hidden = false;
    mbar.innerHTML = '<div class="bt-mbar__t"><span>' + esc(fill(T.pieces, { count: list.length })) + '</span>' + (arr ? '<span class="bt-mbar__arr">' + esc(fill(T.arrives, { date: arr })) + '</span>' : '') +
      (sm.sv && L.saving_short ? '<span class="bt-mbar__sv">' + esc(fill(L.saving_short, { saving: money(sm.sv) })) + '</span>' : '') + '<b data-total>' + money(net) + '</b></div>' +
      '<div class="bt-mbar__b"><button type="button" class="bt-btn2 is-sm" data-chat aria-label="' + esc(T.talk_aria) + '"><span class="bt-live" aria-hidden="true"></span>' + esc(L.talk_short || T.talk_short) + '</button>' +
      '<button type="button" class="bt-btn" data-cart' + (list.length ? '' : ' disabled') + '>' + ic('lock') + esc(L.checkout || '') + '</button></div>';
    var ma = mbar.querySelector('.bt-mbar__arr'); if (ma && ma.scrollWidth > ma.clientWidth) ma.hidden = true;
    if (BI.play && hasLock()) { BI.play = false; buildIn(net); }

    var to = U.toast;
    layer.innerHTML = sheetHTML() + emailHTML() + chatHTML() + (to ? '<div class="bt-toast" role="status" aria-live="polite"><span>' + esc(to.t) + '</span>' +
      (to.undo && L.undo ? '<button type="button" class="bt-toast__u" data-undo>' + esc(L.undo) + '</button>' : '') + '</div>' : '');
    var cb = layer.querySelector('[data-cc-body]'); if (cb) cb.scrollTop = cb.scrollHeight;
    root.classList.toggle('is-locked', !!U.sheet);
    // A focus target inside a shimmering card is hidden: it takes focus when the skeleton ends
    if (U.focus) { var f = root.querySelector(U.focus); if (f) { if (f.closest('.is-skel')) SK.focus = U.focus; else f.focus(); } U.focus = null; }

    if (!viewed) { viewed = true; track('bar_view', { setting: S.set, volume: S.vol, entry: S.entry || '' }); }
  }

  /* ---- content feedback: after a change the cards it touches show a 300ms skeleton (sizes kept), then fade in.
     sel: null = the whole bar (verdict, machine, tiles, plan); else the selectors of the cards to shimmer. ---- */
  var SK = { sel: null, until: 0, t: null, focus: null };
  function skelTargets() { return app.querySelectorAll(SK.sel ? SK.sel.join(',') : '.bt-verdict,.bt-hero,.bt-t,.bt-plan'); }
  function skeleton(sel) {
    SK.sel = sel || null; SK.until = Date.now() + 300;
    clearTimeout(SK.t);
    SK.t = setTimeout(function () {
      SK.until = 0;
      if (!app) return;
      Array.prototype.forEach.call(app.querySelectorAll('.is-skel'), function (el) { el.classList.remove('is-skel'); el.classList.add('is-fade'); });
      // A tile that just moved up is already being scrolled to: focus without a second jump
      if (SK.focus) { var f = root.querySelector(SK.focus); SK.focus = null; if (f) f.focus(f.closest('.bt-flash') ? { preventScroll: true } : undefined); }
    }, 300);
  }
  function skelApply() {
    if (Date.now() >= SK.until) return;
    Array.prototype.forEach.call(skelTargets(), function (el) { el.classList.add('is-skel'); });
  }
  // A change at one station: that card, the plan and the verdict
  function skelAt(h) { skeleton([h === M.MACH ? '.bt-hero' : '.bt-t[data-st="' + h + '"]', '.bt-plan', '.bt-verdict']); }

  /* ---- build-in (an opening with a locked product, first bar render only): the locked piece (and the verdict) first,
     alone, then each filled station in bar order RISE_GAP apart (fade + 8px rise); empty "+" tiles / an empty machine
     card come with the last one. The plan's rows follow the same order and every total ([data-total]) counts up from
     the locked piece's price. Under ~700ms in all. Reduced motion: nothing moves. ---- */
  var RISE = 240, RISE_GAP = 140, RISE_ALL = 700;
  function buildIn(net) {
    if (reduced()) return;
    var main = app.querySelector('.bt-main'); if (!main) return;
    var card = function (h) { return h === M.MACH ? main.querySelector('.bt-hero') : main.querySelector('.bt-t[data-st="' + h + '"]'); };
    var steps = {}, k = 0, late = [], from = 0;
    M.order().forEach(function (h) {
      var el = card(h); if (!el) return;
      if (lockedSt(h)) { steps[h] = 0; var id = M.cur(h); if (id) from += Number(P[id].p) || 0; }
      else if (el.classList.contains('is-empty') || (h === M.MACH && M.machineEmpty())) late.push(h);
      else steps[h] = ++k;
    });
    var gap = k ? Math.min(RISE_GAP, (RISE_ALL - RISE) / k) : 0, end = k * gap;
    var rise = function (el, d) { if (!el) return; el.style.setProperty('--bt-d', Math.round(d) + 'ms'); el.classList.add('bt-rise'); };
    late.forEach(function (h) { steps[h] = k; });
    Object.keys(steps).forEach(function (h) {
      rise(card(h), steps[h] * gap);
      rise(main.querySelector('.bt-plan li[data-st="' + h + '"]'), steps[h] * gap);
    });
    rise(main.querySelector('.bt-verdict'), 0);
    rise(main.querySelector('.bt-lgroup[data-g="yours"] .bt-lgroup__h'), 0);
    rise(main.querySelector('.bt-lgroup[data-g="matched"] .bt-lgroup__h'), gap);
    rise(main.querySelector('.bt-saving'), end);
    // Totals: from the locked piece's price to the bar total over the same time
    var els = root.querySelectorAll('[data-total]'), dur = end + RISE, t0 = null;
    if (!els.length || from >= net) return;
    var paint = function (v) { Array.prototype.forEach.call(els, function (el) { el.textContent = money(v); }); };
    paint(from);
    requestAnimationFrame(function frame(ts) {
      // A later render replaced the totals: they are already right
      if (!els[0].isConnected) return;
      if (t0 == null) t0 = ts;
      var x = Math.min(1, (ts - t0) / dur), e = 1 - Math.pow(1 - x, 3);
      paint(x < 1 ? Math.round((from + (net - from) * e) / 100) * 100 : net);
      if (x < 1) requestAnimationFrame(frame);
    });
  }

  // r25: a station that moved from the add row into the fitted row (U.flash): scrolled into view, one accent ring. The
  // ring is on the tile's <li>, so the tile's own skeleton / fade animation doesn't cut it short
  var flashT;
  function flash(el) {
    var li = el && el.parentNode; if (!li) return;
    el.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
    li.classList.add('bt-flash');
    clearTimeout(flashT);
    flashT = setTimeout(function () { li.classList.remove('bt-flash'); }, 1100);
  }

  // U.toast = { t, undo: a snap() to restore, or null }; with Undo it stays 6 s
  var tt;
  function toast(m, undo) {
    U.toast = { t: m, undo: undo || null }; render(); clearTimeout(tt);
    tt = setTimeout(function () { U.toast = null; render(); }, undo ? 6000 : 2400);
  }

  /* ---- r20: changes we make ourselves are announced. snap() before a change at station h; ruleSwap(h, before) after
     it: another station whose piece a rule changed (the tamper matched to the grinder) gets labels.swap_note with Undo,
     which restores the shopper's choices as they were. ---- */
  function snap(h) {
    var c = {};
    M.order().forEach(function (x) { c[x] = M.inBar(x) ? M.cur(x) : null; });
    return { h: h, cur: c, st: JSON.parse(JSON.stringify({ chosen: S.chosen, have: S.have, added: S.added, pin: S.pin, own: S.own })) };
  }
  function restore(b) { S.chosen = b.st.chosen; S.have = b.st.have; S.added = b.st.added; S.pin = b.st.pin; S.own = b.st.own; }
  function ruleSwap(h, before) {
    var hit = null;
    M.order().forEach(function (x) {
      var a = before.cur[x], b = M.inBar(x) ? M.cur(x) : null;
      if (!hit && x !== h && a && b && a !== b) hit = { h: x, from: a, to: b };
    });
    if (!hit || !L.swap_note) return false;
    var d = (Number(P[hit.to].p) || 0) - (Number(P[hit.from].p) || 0);
    toast(fill(L.swap_note, { station: stLabel(hit.h).toLowerCase(), to: short(hit.to), grinder: short(M.cur(h)), diff: (d < 0 ? '−' : '+') + money(Math.abs(d)) }), before);
    return true;
  }
  // After a volume / setting change: the choices it dropped, one labels.keep_note each
  function keepToast(gone) {
    if (!gone || !gone.length || !L.keep_note) return;
    toast(gone.map(function (g) { return fill(L.keep_note, { piece: short(g.id), volume: volShort(), to: short(M.cur(g.h)) }); }).join(' '));
  }

  /* ---- URL: the current page's own params + bar=1 + the bar (same params as Copy link) ---- */
  function pageParams() { var sp = new URLSearchParams(location.search); KEYS.forEach(function (k) { sp.delete(k); }); return sp.toString(); }
  function cleanUrl() { var q = pageParams(); return location.pathname + (q ? '?' + q : '') + location.hash; }
  function barUrl() {
    var q = pageParams(), sq = M ? M.shareQuery() : '';
    // Past the setup screen: n=0, so a reload, Copy link or shared link opens straight into the bar. On it, v only once
    // a volume is chosen (S.vol is then just the default the rows are listed for)
    if (M && S.vol && !U.need) sq += (sq ? '&' : '') + 'n=0';
    if (M && U.need && !U.need.vol) sq = sq.split('&').filter(function (x) { return x.indexOf('v=') !== 0; }).join('&');
    return location.pathname + '?' + (q ? q + '&' : '') + 'bar=1' + (sq ? '&' + sq : '') + location.hash;
  }
  function syncUrl() {
    var u = barUrl();
    if (u !== location.pathname + location.search + location.hash) { try { history.replaceState(history.state, '', u); } catch (e) {} }
  }
  function shareUrl() { return location.origin + barUrl(); }
  function summary(question) {
    var list = M.items(), s = sums(list), lines = [fill(T.summary_head, { setting: setNoun(), volume: volText() })];
    list.forEach(function (x) { lines.push('- ' + short(x.id) + ': ' + money(P[x.id].p)); });
    M.order().forEach(function (h) { if (!M.open(h) && !M.cur(h) && !(h === M.MACH && M.machineEmpty())) lines.push('- ' + fill(T.plan_own, { station: stLabel(h) })); });
    // Freight, the saving and the plan's total (after the saving, with freight)
    if (s.fr != null && L.freight) lines.push(L.freight + ': ' + freightText(s.fr));
    if (s.sv) lines.push(fill(T.summary_saving, { saving: money(s.sv) }));
    lines.push(fill(T.summary_total, { total: money(s.net) }));
    // Arrival and installation, as the plan panel shows them
    var arr = arrival(list), inst = installLine();
    if (arr) lines.push(fill(T.arrives, { date: arr }));
    if (inst) lines.push(inst);
    lines.push(fill(T.summary_link, { url: shareUrl() }));
    if (question) lines.unshift(fill(T.summary_question, { question: question }));
    return lines.join('\n');
  }

  function mailto(text) {
    if (!D.email) return;
    location.href = 'mailto:' + D.email + '?subject=' + encodeURIComponent(T.summary_subject || '') + '&body=' + encodeURIComponent(text);
  }
  // The site's chat rule lives in assets/footer.js (window.openChatWith): Tidio in staff hours, the night chat outside
  // them. Both windows are hidden while the drawer is open; the hand-off shows them above it (bar-tool.css, html.bt-tidio).
  // The shopper's first message is the question + "My bar: <link>"; the full bar (summary) goes to staff only: Tidio
  // contact properties bar_summary / bar_url, the night chat's question. The new-chat alert (Slack) gets the question
  // with the link, the question cut so the link survives the alert's 280-character limit.
  function handOff(email, question) {
    var url = shareUrl(), mine = fill(T.my_bar, { url: url }), sum = summary('');
    if (typeof window.openChatWith !== 'function') { mailto(summary(question)); return; }
    var room = 278 - mine.length, q = question.length > room ? question.slice(0, Math.max(0, room - 1)) + '…' : question;
    document.documentElement.classList.add('bt-tidio');
    // from: the staff-facing "started from" label (alerts, Tidio contact), as the spec names it
    window.openChatWith({ from: 'Coffee bar tool', question: (q ? q + ' ' : '') + mine, firstMessage: (question ? question + '\n' : '') + mine, email: email, summary: sum, url: url });
  }

  /* ---- "Email me this plan": the Klaviyo client calls of assets/collection-compare.js ("Email this comparison"):
     a subscription to the store list (custom_source "Coffee bar tool") and the event "Coffee Bar Plan Requested",
     whose flow sends the plan to the shopper and alerts staff. Key / list: bar-data "klaviyo" (doc_request). ---- */
  var EMAIL_KEY = 'bt_email';
  function knownEmail() { try { return window.sessionStorage.getItem(EMAIL_KEY) || ''; } catch (e) { return ''; } }
  function keepEmail(v) { try { window.sessionStorage.setItem(EMAIL_KEY, v); } catch (e) {} }
  function abs(u) { try { return u ? new URL(u, location.origin).href : ''; } catch (e) { return u || ''; } }
  function post(path, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(D.klaviyo.key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify(payload)
    }).then(function (r) { if (!r.ok) throw new Error(r.status); });
  }
  // One retry 1.5 s after a failed call (network / CORS error or non-2xx); a call that succeeded is never repeated
  function postRetry(path, payload) {
    return post(path, payload).catch(function () {
      return new Promise(function (r) { setTimeout(r, 1500); }).then(function () { return post(path, payload); });
    });
  }
  // The event's properties; the email template reads these exact names
  function planProps() {
    var list = M.items(), s = sums(list), tot = s.tot, sv = s.sv;
    var items = list.map(function (x) { var p = P[x.id]; return { station: stLabel(x.h), name: short(x.id), price: money(p.p), image: abs(p.img), url: abs(p.u) }; });
    return {
      value: tot / 100,
      properties: {
        setting: S.set,
        setting_label: (CH.setting_noun || {})[S.set] || setNoun(),
        volume_label: volText(),
        items: items,
        items_text: items.map(function (x) { return x.name + ' (' + x.price + ')'; }).join(', '),
        piece_count: list.length,
        total: money(tot),
        saving: sv ? money(sv) : '',
        freight: freightText(s.fr),
        // The plan's total: after the saving, with freight
        total_after: money(s.net),
        bar_url: shareUrl(),
        checkout_url: abs(M.checkoutUrl()),
        page_url: location.origin + location.pathname,
        verdict: verdictData().title || ''
      }
    };
  }
  function sendPlan(email) {
    var K = D.klaviyo || {};
    if (!K.key || !K.list || !window.fetch) return Promise.reject(new Error('klaviyo'));
    var pl = planProps(), profile = { data: { type: 'profile', attributes: { email: email } } };
    // Both calls settle (retry included) before the sheet shows the result, so the button stays disabled meanwhile
    var calls = [
      postRetry('subscriptions', { data: { type: 'subscription',
        attributes: { profile: profile, custom_source: 'Coffee bar tool' },
        relationships: { list: { data: { type: 'list', id: K.list } } } } }),
      postRetry('events', { data: { type: 'event', attributes: {
        properties: pl.properties, value: pl.value, value_currency: 'USD',
        metric: { data: { type: 'metric', attributes: { name: 'Coffee Bar Plan Requested' } } },
        profile: profile } } })
    ].map(function (p) { return p.then(function () { return true; }, function () { return false; }); });
    return Promise.all(calls).then(function (ok) { if (ok.indexOf(false) > -1) throw new Error('klaviyo'); });
  }

  /* ---- drawer: build, open, close ---- */
  var X_IC = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9"/></svg>';
  var built = false;
  function build() {
    root.innerHTML = '<div class="bt-dr__top"><h2 class="bt-dr__title" id="bt-dr-title"></h2><div class="bt-dr__ctl" data-bar-ctl hidden></div>' +
      '<button type="button" class="bt-dr__x" data-dr-close aria-label="' + esc(T.close_tool || T.close) + '">' + X_IC + '</button></div>' +
      '<div class="bt-dr__body" data-bar-body><div class="home-wrap bt"><div class="bt-app" data-bar-app aria-live="polite"></div></div></div>' +
      '<div class="bt-mbar" data-bar-mbar hidden></div><div data-bar-layer></div>';
    titleEl = root.querySelector('#bt-dr-title'); ctlEl = root.querySelector('[data-bar-ctl]'); bodyEl = root.querySelector('[data-bar-body]');
    app = root.querySelector('[data-bar-app]'); mbar = root.querySelector('[data-bar-mbar]'); layer = root.querySelector('[data-bar-layer]');
    root.addEventListener('click', onClick);
    root.addEventListener('submit', onSubmit);
    // Icons the header's <template> doesn't carry (verdict line) come with the data
    Object.keys(D.icons || {}).forEach(function (k) { if (!IC[k] && D.icons[k]) IC[k] = D.icons[k]; });
    // The fixed control widths depend on the web font: measure again once it has loaded, and keep the pills on their
    // buttons when the window is resized
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measured = false; if (isOpen && M && M.setting()) { measureCtl(); pills(); } });
    window.addEventListener('resize', function () { if (isOpen && M && M.setting()) pills(); });
    built = true;
  }

  function reduced() { return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches); }

  /* ---- click feedback: the clicked tile / link gets a clay loading bar (.bt-ld.is-loading, CSS inline in the
     bar-tool.liquid shell and in bar-entry.css, so it shows on a cold first tap) until the next screen is rendered,
     for at least LOAD_MIN so it is always seen ---- */
  var LOAD_MIN = 350;
  function loading(el) { if (el && el.classList) el.classList.add('bt-ld', 'is-loading'); return Date.now(); }
  function loaded(el) { if (el && el.classList) el.classList.remove('is-loading'); }
  function minWait(t0) { return new Promise(function (r) { setTimeout(r, Math.max(0, LOAD_MIN - (Date.now() - t0))); }); }

  // A link that already carries a bar (shared / copied / chat): n=0, or bar=1 with a volume and a locked product or changes
  function skipNeed(query, bar) { return query.n === '0' || !!(bar && query.v && (query.m || query.c)); }

  // o.push: add a history entry (link clicks); o.href: where to go if the data can't load; o.loader / o.t0: the
  // clicked link showing the loading bar and when it started; o.bar: the URL had bar=1; o.pop: history Back / Forward;
  // o.need: the setup screen's saved state (history entry)
  function openBar(query, o) {
    o = o || {};
    if (!isOpen && !o.keepOpener) opener = o.opener || document.activeElement;
    return Promise.all([loadCss(), loadData(), o.t0 ? minWait(o.t0) : null]).then(function () {
      mergePageProducts();
      T = D.t || {}; L = D.labels || {}; CH = D.chat || {};
      initFmt();
      if (!built) build();
      var m = createModel(D, query);
      if (!m.SETS.length || !m.MACH) { if (o.href) location.href = o.href; return; }
      M = m; S = M.S; P = M.P;
      U = { sheet: null, email: null, chat: null, toast: '', focus: null, back: null, need: null };
      // A setting with no volume (product-page button, menu card, header button): the setup screen first. A volume in
      // the link (collection / home band tiles with v, shared links) opens straight into the bar. Back to the setup's
      // own history entry: the setup again.
      if (S.set) {
        if (o.need) startSetup('vol' in o.need ? o.need.vol : null);
        else if (!S.vol && !skipNeed(query, o.bar)) startSetup(null);
      }
      SK.until = 0;
      viewed = false;
      var lk = !!(query.m || query.g);
      BI = { play: lk, fresh: lk, all: false, touched: {} };
      show(o.push);
      loaded(o.loader);
      // Not in the stations and not on this page's cards (e.g. a shared link opened elsewhere): the storefront .js
      if ((query.m && !P[query.m]) || (query.g && !P[query.g])) {
        Promise.all([ensure(query.m), ensure(query.g)]).then(function () {
          if (M !== m) return;
          if (query.m && P[query.m] && !S.lockM) S.lockM = query.m, S.viewing[M.MACH] = true;
          if (query.g && P[query.g] && !S.lockG) S.lockG = query.g, S.viewing[M.GR] = true;
          render();
        });
      }
    }).catch(function () { if (o.href) location.href = o.href; });
  }

  function show(push) {
    clearTimeout(closeT);
    if (!isOpen && push) hpush();
    else if (U.need) saveNeed();
    isOpen = true;
    root.hidden = false;
    root.classList.remove('is-out');
    root.classList.add('is-in');
    document.documentElement.classList.add('bt-dr-open');
    if (bodyEl) bodyEl.scrollTop = 0;
    render();
    var x = root.querySelector(U.need ? '#bt-su-t' : '[data-dr-close]'); if (x) x.focus();
  }

  function hide() {
    if (!isOpen) return;
    isOpen = false;
    U.sheet = null; U.email = null; U.chat = null;
    document.documentElement.classList.remove('bt-dr-open', 'bt-tidio');
    root.classList.remove('is-in', 'is-locked');
    root.classList.add('is-out');
    closeT = setTimeout(function () { root.hidden = true; root.classList.remove('is-out'); }, reduced() ? 0 : 200);
    var back = opener; opener = null;
    if (back && back.focus && document.contains(back) && back.getClientRects().length) back.focus();
  }

  // Close button / Esc: step back over all our own history entries (question, step, bar; popstate hides), else just
  // clean the URL
  // The tool page (/pages/coffee-bar) has nothing under the drawer: Close goes back past it when the previous page is
  // on this site, else to the home page
  var closing = false;
  function onToolPage() { return !!PATH && location.pathname.slice(-PATH.length) === PATH; }
  function requestClose() {
    var st = history.state;
    if (onToolPage()) {
      var d = st && st.bar ? Number(st.d) || 1 : 0, ref = '';
      try { ref = document.referrer ? new URL(document.referrer).origin : ''; } catch (e) {}
      if (ref === location.origin && history.length > d + 1) history.go(-(d + 1));
      else location.href = (D && D.root) || '/';
      return;
    }
    if (st && st.bar) { closing = true; history.go(-(Number(st.d) || 1)); }
    else {
      st = Object.assign({}, st || {}); delete st.need;
      try { history.replaceState(st, '', cleanUrl()); } catch (e) {}
      hide();
    }
  }

  window.addEventListener('popstate', function () {
    var sp = new URLSearchParams(location.search), st = history.state || {};
    // Close landed on the entry before ours: a page that was itself opened with the drawer (bar=1 / the tool page)
    // stays closed, its URL cleaned
    if (closing) {
      closing = false;
      if (sp.get('bar') === '1') { st = Object.assign({}, st); delete st.need; try { history.replaceState(st, '', cleanUrl()); } catch (e) {} }
      hide();
      return;
    }
    if (sp.get('bar') === '1') openBar(queryOf(sp), { keepOpener: isOpen, pop: true, need: st.need });
    else hide();
  });

  // Close the phone menu drawer / desktop mega panel the link sat in, so they aren't left open under the tool
  function closeMenus(a) {
    if (a.closest('.mobile-menu')) { var cb = document.getElementById('mobile-menu-close'); if (cb) cb.click(); }
    if (document.documentElement.classList.contains('mega-panel-open')) document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }

  // One delegated listener: a plain left click on any link to the tool page opens the drawer in place
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || root.contains(a)) return;
    var u = toolUrl(a); if (!u) return;
    e.preventDefault();
    closeMenus(a);
    // From the phone menu, focus returns to the hamburger (closing the menu just focused it), not to the hidden link
    var from = a.closest('.mobile-menu') ? document.activeElement : a;
    var t0 = loading(a);
    openBar(queryOf(u.searchParams), { push: true, opener: from, href: u.href, loader: a, t0: t0 });
  });

  // Preload on intent (hover / focus / touch of a tool link) and when an entry card comes into view
  ['pointerover', 'focusin', 'touchstart'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (dataP && cssP) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (a && toolUrl(a)) preload();
    }, { passive: true, capture: true });
  });
  if ('IntersectionObserver' in window) {
    var entries = document.querySelectorAll('[data-bar-entry]');
    if (entries.length) {
      var io = new IntersectionObserver(function (list) {
        if (!list.some(function (x) { return x.isIntersecting; })) return;
        io.disconnect();
        (window.requestIdleCallback || function (f) { setTimeout(f, 300); })(preload);
      }, { rootMargin: '200px 0px' });
      Array.prototype.forEach.call(entries, function (el) { io.observe(el); });
    }
  }

  /* ---- checkout (r20): add the bar to the shopper's cart (/cart/add.js, then the _bar attribute via /cart/update.js)
     and go to /checkout with the bar's code; if the add fails, the permalink (which replaces the cart) ---- */
  var cartBusy = false;
  function checkout(add, fallback) {
    cartBusy = true;
    var base = (D.root || '/').replace(/\/?$/, '/');
    var post = function (path, body) {
      return fetch(base + path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); });
    };
    var go = function (u) { location.href = u; setTimeout(function () { cartBusy = false; }, 4000); };
    post('cart/add.js', { items: add.items })
      .then(function () { return post('cart/update.js', { attributes: add.attributes }).catch(function () {}); })
      .then(function () { go(add.go); }, function () { go(fallback); });
  }

  /* ---- events inside the drawer ---- */
  var qBusy = false;
  function onClick(e) {
    var t = e.target.closest('button,[data-close]');
    if (!t || !root.contains(t)) return;
    if (t.classList.contains('bt-scrim') && e.target !== t) return;
    // Setting and volume tiles: the loading bar first, then the next screen (at least LOAD_MIN later)
    if (t.classList.contains('bt-qt')) {
      if (qBusy) return;
      qBusy = true;
      var m0 = M;
      minWait(loading(t)).then(function () { qBusy = false; if (M === m0 && isOpen) act(t); });
      return;
    }
    act(t);
  }
  function act(t) {
    var d = t.dataset;
    var inTop = !!t.closest('.bt-dr__top');
    var plan = !!(S.set && S.vol);
    // On the setup screen, power / water only redraw it (no skeleton, the build-in stays)
    var bar = plan && !U.need;
    if ('drClose' in d) { requestClose(); return; }
    if (d.svol) { needGo(d.svol); return; }
    else if (d.set) {
      // In the bar, a new setting opens on its default volume; from the setting question or the setup screen, the
      // setup screen with nothing selected
      if (d.set !== S.set) {
        M.setSetting(d.set);
        if (bar && M.setting()) { var gone0 = M.setVolume(dvol()); skeleton(); changed(); if (gone0.length) { if (inTop) U.focus = '.bt-dr__top [data-set="' + d.set + '"]'; keepToast(gone0); return; } }
        else if (M.setting()) { startSetup(null); saveNeed(); U.focus = inTop ? null : '#bt-su-t'; if (bodyEl) bodyEl.scrollTop = 0; }
      }
      if (inTop) U.focus = '.bt-dr__top [data-set="' + d.set + '"]';
    }
    else if ('reset' in d) { S.set = null; S.vol = null; U.need = null; }
    else if (d.vol) {
      var newVol = bar && d.vol !== S.vol;
      if (newVol) { skeleton(); changed(); }
      var gone1 = M.setVolume(d.vol);
      U.focus = inTop ? '.bt-dr__top [data-vol="' + d.vol + '"]' : null;
      if (newVol && gone1.length) { keepToast(gone1); return; }
    }
    else if (d.toggle === 'power') { S.power = S.power === '110v' ? '240v' : '110v'; if (bar) { skeleton(); changed(); } U.focus = (inTop ? '.bt-dr__top ' : '.bt-app ') + '[data-toggle="power"]'; }
    else if (d.toggle === 'water') { S.water = S.water === 'plumbed' ? 'tanks' : 'plumbed'; if (bar) { skeleton(); changed(); } U.focus = (inTop ? '.bt-dr__top ' : '.bt-app ') + '[data-toggle="water"]'; }
    else if (d.swap || d.choose) { U.sheet = d.swap || d.choose; U.back = '[data-' + (d.swap ? 'swap' : 'choose') + '="' + U.sheet + '"]'; U.focus = '.bt-sheet .bt-opt'; }
    else if (d.pick) {
      var h = U.sheet, from = M.cur(h), b0 = snap(h), up = M.open(h);
      M.choose(h, d.pick); U.sheet = null; U.focus = U.back; changed(h);
      // From the add row: the piece moves up into the fitted row (its "+" card is gone), focus its Change button
      if (up) { U.flash = h; U.focus = '.bt-t[data-st="' + h + '"] [data-swap]'; }
      track('bar_change', { station: h, from: from || '', to: d.pick });
      skelAt(h);
      if (!ruleSwap(h, b0)) toast(T.changed);
      return;
    }
    // The small-machine verdict's "Switch to the {to}": the volume's default machine in one tap
    else if (d.mswap) {
      var mh = M.MACH, mfrom = M.cur(mh), b1 = snap(mh);
      M.choose(mh, d.mswap); changed(mh); skelAt(mh);
      track('bar_change', { station: mh, from: mfrom || '', to: d.mswap });
      U.focus = '[data-swap="' + mh + '"]';
      if (ruleSwap(mh, b1)) return;
    }
    else if (d.have) { var hv = d.have, b2 = snap(hv); track('bar_change', { station: hv, from: M.cur(hv) || '', to: '' }); M.haveOne(hv); U.sheet = null; changed(hv); skelAt(hv); if (ruleSwap(hv, b2)) return; }
    else if (d.back) { var b3 = snap(d.back); M.addBack(d.back); changed(d.back); skelAt(d.back); if (ruleSwap(d.back, b3)) return; }
    else if (d.remove) { var b4 = snap(d.remove); track('bar_change', { station: d.remove, from: M.cur(d.remove) || '', to: '' }); M.remove(d.remove); changed(d.remove); skelAt(d.remove); if (ruleSwap(d.remove, b4)) return; }
    else if ('undo' in d) {
      var ub = U.toast && U.toast.undo;
      clearTimeout(tt); U.toast = null;
      if (ub) { restore(ub); changed(ub.h); skeleton(); }
    }
    else if ('close' in d) { U.sheet = null; U.focus = U.back || null; }
    else if ('email' in d) { U.email = { value: knownEmail() }; U.chat = null; U.focus = '#bt-em'; }
    else if ('eclose' in d) { U.email = null; U.focus = '[data-email]'; }
    else if ('copy' in d) {
      var url = shareUrl();
      try { navigator.clipboard.writeText(url).catch(function () {}); } catch (err) {}
      toast(T.copied); return;
    }
    else if ('cart' in d) {
      var list = M.items(), c = M.code(list), add = M.checkoutAdd(), go = M.checkoutUrl(); if (!add || !go || cartBusy) return;
      track('bar_checkout', { setting: S.set, volume: S.vol, total: M.total(list) / 100, saving: c ? c.saving : 0, pieces: list.length });
      checkout(add, go); return;
    }
    else if ('chat' in d) { U.chat = { step: 1 }; U.email = null; U.focus = '.bt-cc-chip'; track('bar_talk', { setting: S.set, volume: S.vol }); }
    else if ('cclose' in d) { U.chat = null; }
    else if ('cown' in d) { var i = root.querySelector('#bt-cc-q'); if (i) { i.focus(); i.classList.add('is-pulse'); setTimeout(function () { i.classList.remove('is-pulse'); }, 900); } return; }
    else if (d.cq != null) { U.chat.q = chatQuestions()[Number(d.cq)] || ''; U.chat.step = 2; U.focus = '#bt-cc-em'; }
    else return;
    render();
  }

  function onSubmit(e) {
    var f = e.target, k = f.getAttribute('data-form'); if (!k) return;
    e.preventDefault();
    var inp = f.querySelector('input'), v = inp ? inp.value.trim() : '';
    if (k === 'chat-q') { if (!v) return; U.chat.q = v; U.chat.step = 2; U.focus = '#bt-cc-em'; render(); return; }
    if (!/^\S+@\S+\.\S+$/.test(v)) { inp.setCustomValidity(T.email_invalid); inp.reportValidity(); inp.setCustomValidity(''); return; }
    keepEmail(v);
    if (k === 'chat-email') { var q = U.chat.q || ''; U.chat = null; render(); handOff(v, q); return; }
    if (k === 'email' && U.email && !U.email.busy) {
      var em = U.email, m0 = M;
      em.value = v; em.busy = true; em.err = false; render();
      sendPlan(v).then(function () {
        if (U.email !== em || M !== m0) return;
        em.busy = false; em.done = v; U.focus = '.bt-em .bt-ok'; render();
        track('bar_email', { setting: S.set, volume: S.vol, total: M.total() / 100, pieces: M.items().length });
      }).catch(function () {
        if (U.email !== em) return;
        em.busy = false; em.err = true; U.focus = '#bt-em'; render();
      });
    }
  }

  // Esc closes the top layer (sheet → chat → drawer); Tab stays inside the drawer (or the open sheet)
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      if (U.sheet) { U.sheet = null; U.focus = U.back || null; render(); }
      else if (U.email) { U.email = null; U.focus = '[data-email]'; render(); }
      else if (U.chat) { U.chat = null; render(); }
      else requestClose();
      return;
    }
    if (e.key !== 'Tab') return;
    var scope = root.querySelector('.bt-sheet') || root;
    var items = Array.prototype.filter.call(scope.querySelectorAll(FOCUSABLE), function (x) { return x.getClientRects().length > 0; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1], act = document.activeElement;
    if (e.shiftKey && (act === first || !scope.contains(act))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (act === last || !scope.contains(act))) { e.preventDefault(); first.focus(); }
  });

  /* ---- open on load: shared / copied / chat links (bar=1) and the tool page itself ---- */
  var sp0 = new URLSearchParams(location.search);
  // A reload on the setup screen keeps its choices (its history state survives the reload)
  if (sp0.get('bar') === '1' || (PATH && location.pathname.slice(-PATH.length) === PATH)) openBar(queryOf(sp0), { bar: sp0.get('bar') === '1', need: history.state && history.state.need });
})();
